<script setup lang="ts">
import { computed, watch, ref } from 'vue';
import { useForm } from 'vee-validate';
import { z } from 'zod';
import { useClipboard } from '@vueuse/core';
import { toTypedSchema } from '@vee-validate/zod';
import { useI18n } from 'vue-i18n';
import AppInput from './AppInput.vue';
import AppTextarea from './AppTextarea.vue';
import IconCopy from '@/assets/copy-icon.svg?component';
import { useUnsavedChanges } from '@/composables/useUnsavedChanges';
import { Client, ClientMeta } from '@/models/Client';
import AppButton from '@/components/AppButton.vue';
import { zStringReq } from '@/utilities/Helpers';

const props = withDefaults(defineProps<{
	editable?: boolean;
	client?: Client | null;
}>(), {
	editable: false,
	client: null,
});

const emit = defineEmits<{
	(e: 'cancel'): void;
	(e: 'save', client: ClientMeta): void;
}>();

const isNewClient = computed(() => !props.client);

const { t } = useI18n();

const validationSchema = toTypedSchema(
	z.object({
		name: zStringReq(t('validation.fieldIsRequired')),
		description: z.string().nullable().optional(),
	}),
);

const {
	meta, values, setValues,
} = useForm<ClientMeta>({
	validationSchema,
	initialValues: {
		name: props.client?.name ?? '',
		description: props.client?.description ?? '',
	},
	keepValuesOnUnmount: true,
});

watch(() => props.editable, () => {
	setValues({
		name: props.client?.name ?? '',
		description: props.client?.description ?? '',
	});
});

const { toggle, remove } = useUnsavedChanges();

watch(() => meta.value.dirty, toggle);

const cancel = () => {
	remove();
	emit('cancel');
};

const onConfirm = () => {
	remove();
	emit('save', { name: values.name, description: values.description, isInternal: props.client?.isInternal ?? false });
};

const source = ref(props.client?.id ?? '');

const {
	copy, copied, isSupported,
} = useClipboard({ source });
</script>

<template>
	<form>
		<div class="text-xl mb-3">
			{{ $t('admin.clientName') }}
		</div>
		<AppInput
			v-if="editable"
			:placeholder="$t('admin.clientName')"
			name="name"
		/>
		<div
			v-else
			class="text-2xl font-semibold mb-2"
		>
			{{ client?.name }}
		</div>

		<p class="text-gray mb-14 inline-block mt-12 mr-2">
			{{ client?.id }}
		</p>
		<div
			v-if="isSupported && client?.id"
			class="inline-block"
		>
			<IconCopy
				class="inline-block w-4 mr-2 fill-primary-100 hover:cursor-pointer hover:fill-primary-200"
				@click="copy(source)"
			/>
			<div
				v-if="copied"
				class="text-gray text-xs inline-block"
			>
				{{ $t('admin.copied') }}
			</div>
		</div>
		<div class="text-xl mb-3">
			{{ $t('common.description') }}
		</div>
		<AppTextarea
			v-if="editable"
			name="description"
			:placeholder="$t('common.optional')"
			:label="$t('common.description')"
			:rows="5"
		/>
		<p
			v-else-if="client?.description"
			class="text-gray"
		>
			{{ client.description }}
		</p>
		<p
			v-else
			class="text-gray italic"
		>
			{{ $t('admin.noClientDescription') }}
		</p>

		<div
			v-if="editable"
			class="flex justify-between mt-12"
		>
			<AppButton
				size="sm"
				:disabled="!meta.valid"
				@click="onConfirm()"
			>
				<span v-if="isNewClient">
					{{ $t('admin.createClient') }}
				</span>
				<span v-else>
					{{ $t('common.save') }}
				</span>
			</AppButton>
			<AppButton
				size="sm"
				color="outline"
				@click="cancel"
			>
				{{ $t('common.cancel') }}
			</AppButton>
		</div>
	</form>
</template>
